import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CloudSun, Droplets, Wind, X, Sunrise, Sunset, AlertTriangle } from 'lucide-react';

/**
 * Live field weather for the dashboard.
 *
 * Pulls current conditions, a 7-day forecast and the next 24 hours from Open-Meteo (no API key,
 * free for this use) every time the dashboard loads, so it is never stale. Clicking the card
 * opens the full forecast.
 */

const LAT = 32.4876;
const LON = -84.9153;
const URL =
  `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}` +
  `&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m` +
  `&hourly=temperature_2m,precipitation_probability,weather_code` +
  `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset` +
  `&timezone=America%2FNew_York&forecast_days=7&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch`;

// WMO weather codes -> label + emoji
function describe(code: number): { label: string; emoji: string } {
  if (code === 0) return { label: 'Clear', emoji: '☀️' };
  if (code <= 2) return { label: 'Mostly clear', emoji: '🌤️' };
  if (code === 3) return { label: 'Overcast', emoji: '☁️' };
  if (code <= 48) return { label: 'Fog', emoji: '🌫️' };
  if (code <= 57) return { label: 'Drizzle', emoji: '🌦️' };
  if (code <= 67) return { label: 'Rain', emoji: '🌧️' };
  if (code <= 77) return { label: 'Snow', emoji: '🌨️' };
  if (code <= 82) return { label: 'Showers', emoji: '🌧️' };
  if (code <= 86) return { label: 'Snow showers', emoji: '🌨️' };
  return { label: 'Thunderstorms', emoji: '⛈️' };
}

function windWarning(mph: number): string | null {
  if (mph >= 25) return `High wind advisory — gusts beyond ${Math.round(mph)} mph expected. Secure gear and avoid elevated work.`;
  if (mph >= 18) return `Breezy — ${Math.round(mph)} mph winds. Watch limb movement on removals.`;
  return null;
}

const WeatherWidget: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(URL, { cache: 'no-store' })
      .then(r => r.json())
      .then(j => { if (alive && j && j.current) { setData(j); setFetchedAt(new Date()); } })
      .catch(() => alive && setError('Weather unavailable'));
    return () => { alive = false; };
  }, []);

  if (error || !data) {
    return (
      <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 rounded-2xl p-6">
        <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">Columbus Field Weather</h4>
        <p className="text-sm text-stone-500 font-bold">{error || 'Loading…'}</p>
      </div>
    );
  }

  const cur = data.current;
  const today = { high: Math.round(data.daily.temperature_2m_max[0]), low: Math.round(data.daily.temperature_2m_min[0]), rain: data.daily.precipitation_probability_max[0] };
  const desc = describe(cur.weather_code);
  const warning = windWarning(cur.wind_speed_10m);

  // next 24h, starting from the current hour
  const nowIdx = Math.max(0, data.hourly.time.findIndex((t: string) => new Date(t) >= new Date()));
  const next24 = data.hourly.time.slice(nowIdx, nowIdx + 24).map((t: string, i: number) => ({
    time: t,
    temp: Math.round(data.hourly.temperature_2m[nowIdx + i]),
    rain: data.hourly.precipitation_probability[nowIdx + i],
  }));

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full text-left bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 rounded-2xl p-6 hover:shadow-md hover:border-sky-200 transition cursor-pointer"
        title="Open the full forecast"
      >
        <div className="flex items-start justify-between mb-4">
          <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Columbus Field Weather</h4>
          <span className="text-[10px] font-bold text-sky-500 uppercase tracking-wider">7-day →</span>
        </div>
        <div className="flex items-center space-x-4 mb-3">
          <span className="text-5xl">{desc.emoji}</span>
          <div>
            <span className="text-5xl font-black text-bark">{Math.round(cur.temperature_2m)}°</span>
            <span className="block text-lg font-bold text-palm">{desc.label}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-stone-500 font-bold mb-3">
          <span className="flex items-center gap-1"><Droplets size={13} /> {cur.relative_humidity_2m}%</span>
          <span className="flex items-center gap-1"><Wind size={13} /> {Math.round(cur.wind_speed_10m)} mph</span>
          <span>H {today.high}° / L {today.low}°</span>
        </div>
        {warning && (
          <p className="flex items-start gap-2 text-[11px] font-bold leading-tight bg-amber-50 border border-amber-100 text-amber-700 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {warning}
          </p>
        )}
        {!warning && today.rain >= 50 && (
          <p className="flex items-start gap-2 text-[11px] font-bold leading-tight bg-sky-50 border border-sky-100 text-sky-700 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {today.rain}% chance of rain today — plan accordingly.
          </p>
        )}
        {fetchedAt && (
          <p className="text-[10px] text-stone-400 mt-3">Updated {fetchedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-4 pt-12 pb-12"
            onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CloudSun size={22} />
                  <div>
                    <h3 className="font-black">Columbus Field Forecast</h3>
                    <p className="text-xs text-sky-100">4314 Fay Dr area · updated {fetchedAt?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition"><X size={18} /></button>
              </div>

              <div className="p-6 space-y-6">
                {/* Next 24 hours */}
                <div>
                  <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3">Next 24 hours</h4>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {next24.map((h: any, i: number) => (
                      <div key={i} className="shrink-0 w-16 text-center bg-stone-50 rounded-xl py-3 border border-stone-100">
                        <p className="text-[10px] font-bold text-stone-400">{new Date(h.time).toLocaleTimeString([], { hour: 'numeric' })}</p>
                        <p className="text-lg my-1">{describe(data.hourly.weather_code[nowIdx + i]).emoji}</p>
                        <p className="text-sm font-black text-bark">{h.temp}°</p>
                        <p className="text-[10px] text-sky-500 font-bold">{h.rain}%</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 7 day */}
                <div>
                  <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3">7-day outlook</h4>
                  <div className="space-y-2">
                    {data.daily.time.map((t: string, i: number) => {
                      const d = describe(data.daily.weather_code[i]);
                      return (
                        <div key={t} className="flex items-center justify-between bg-stone-50 rounded-xl px-4 py-2.5 border border-stone-100">
                          <span className="text-sm font-bold text-bark w-24">{i === 0 ? 'Today' : new Date(t + 'T12:00:00').toLocaleDateString([], { weekday: 'long' })}</span>
                          <span className="text-lg">{d.emoji}</span>
                          <span className="text-xs font-bold text-stone-500 w-24 text-center">{d.label}</span>
                          <span className="text-sm font-black text-bark">{Math.round(data.daily.temperature_2m_max[i])}° / {Math.round(data.daily.temperature_2m_min[i])}°</span>
                          <span className="text-xs font-bold text-sky-500 w-14 text-right">{data.daily.precipitation_probability_max[i]}% rain</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Sun */}
                <div className="flex items-center gap-6 text-xs font-bold text-stone-500">
                  <span className="flex items-center gap-1.5"><Sunrise size={14} className="text-amber-500" /> Sunrise {new Date(data.daily.sunrise[0]).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                  <span className="flex items-center gap-1.5"><Sunset size={14} className="text-orange-500" /> Sunset {new Date(data.daily.sunset[0]).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                </div>

                <p className="text-[10px] text-stone-400">Live data from Open-Meteo, refreshed every time the dashboard loads.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WeatherWidget;
